import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QCenter, QNotFound, QSpinner, useCurrentUser } from '@qualio/ui-components';
import FormTemplateEditor from '../../../components/FormTemplateEditor/FormTemplateEditor';
import { useEventTemplateDetails, useFormTemplateDetails } from '../../../hooks';
import { useUpdateFormTemplate } from './hooks';
import { EventTemplateDetailsResponse, EventTemplateStep } from '../../../types';
import { useRegistryCategories } from '../../../hooks/useRegistryCategories';

const FormEditorOverview: React.FC = () => {
  const { eventTemplateId, formTemplateId } = useParams();
  const navigate = useNavigate();
  const { companyId } = useCurrentUser();
  const { data: registryCategories } = useRegistryCategories();

  // This should not be possible, but ensuring it allows typescript to narrow the type
  if (!eventTemplateId || !formTemplateId) {
    throw new Error('Missing eventTemplateId or formTemplateId');
  }

  const {
    data: eventTemplate,
    isLoading: isEventTemplateLoading,
    isError: isEventTemplateError,
  } = useEventTemplateDetails(companyId, Number(eventTemplateId));

  const step = (eventTemplate as EventTemplateDetailsResponse)?.steps.find(
    (step: EventTemplateStep) => step.form_template_id === Number(formTemplateId),
  );

  const {
    data: formTemplate,
    isLoading: isFormTemplateLoading,
    isIdle,
    isFetchedAfterMount,
    isError: isFormTemplateError,
  } = useFormTemplateDetails(companyId, Number(eventTemplateId), step?.id, Number(formTemplateId), !!eventTemplate);

  const { mutate: updateTemplate } = useUpdateFormTemplate();

  // The event template or form template couldn't be fetched (e.g. a 404 for a deleted / non-existent
  // template, or another fetch failure after retries). Show the not-found page instead of spinning.
  if ((isEventTemplateError && !eventTemplate) || (isFormTemplateError && !formTemplate)) {
    return <QNotFound data-cy="form-template-not-found" />;
  }

  // The event template loaded, but the requested form template isn't one of its steps (e.g. a
  // deleted or mismatched id). The form-template query stays disabled and never 404s, so guard it
  // here to render the not-found page instead of spinning forever.
  if (eventTemplate && !step) {
    return <QNotFound data-cy="form-template-not-found" />;
  }

  if (
    isEventTemplateLoading ||
    isFormTemplateLoading ||
    isIdle ||
    !isFetchedAfterMount ||
    !registryCategories ||
    !step ||
    !formTemplate ||
    !eventTemplate
  ) {
    return (
      <QCenter pt={10}>
        <QSpinner />
      </QCenter>
    );
  }

  return (
    <FormTemplateEditor
      formStepName={step.label}
      formTemplateId={Number(formTemplateId)}
      eventTemplateId={Number(eventTemplateId)}
      eventTemplateStatus={eventTemplate.status!}
      fieldsArray={formTemplate.fields}
      registryCategories={registryCategories.items}
      onSave={(data) =>
        updateTemplate(
          {
            companyId,
            eventTemplateId: step.workflow_id,
            stepId: step.id,
            formTemplateId: Number(formTemplateId),
            newFields: data,
            formTemplateStatus: formTemplate.status,
          },
          { onSuccess: () => navigate(`/templates/${eventTemplateId}`) },
        )
      }
    />
  );
};

export default FormEditorOverview;
